import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { hebergements } from '../data/mock';
import { ArrowLeft, Star, Phone, MapPin, Users, Check, DollarSign, Euro } from 'lucide-react';
import { Button } from '../components/ui/button';

const HebergementDetail = () => {
  const { slug } = useParams();
  console.log('url', useParams());
  
  const hebergement = Object.values(hebergements).find(h => h.slug === slug);
  const isCamping = slug?.includes('camping') || slug?.includes('pegue');
  const contactCategory = isCamping ? 'pegue' : 'draille';

  if (!hebergement) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Hébergement non trouvé</h1>
          <Link to="/hebergements" className="text-[#afab53] hover:underline">
            Retour aux hébergements
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[70vh]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${hebergement.images[1]})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        </div>
        <div className="relative h-full flex items-end">
          <div className="max-w-7xl mx-auto px-4 pb-16 w-full">
            <Link 
              to="/hebergements" 
              className="inline-flex items-center gap-2 text-white mb-6 hover:text-[#afab53] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour aux hébergements</span>
            </Link>
            {hebergement.classification && (
              <div className="flex items-center gap-3 mb-4">
                {hebergement.classification.includes('épis') ? (
                  <div className="flex items-center gap-1">
                    {[...Array(3)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-white text-white" />
                    ))}
                  </div>
                ) : null}
                <span className="text-white bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                  {hebergement.classification}
                </span>
              </div>
            )}
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">{hebergement.name}</h1>
            <p className="text-xl text-gray-200 max-w-3xl">{hebergement.type}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Bienvenue</h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {hebergement.description}
                </p>
              </div>

              {/* Additional Info */}
              <div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Un emplacement idéal</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Notre situation géographique privilégiée fait de Meyrueis le point de départ idéal pour découvrir les richesses naturelles de la région. Les Gorges du Tarn, de la Jonte et de la Dourbie, le Mont Aigoual, les Grands Causses... Un territoire d'exception vous attend.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Profitez de votre séjour pour visiter les sites remarquables à proximité : la Grotte de Dargilan, le climatographe du Mont Aigoual, l'Aven Armand, l'Abîme de Bramabiau, ou encore le célèbre Viaduc de Millau.
                </p>
              </div>
              {/* Image Gallery */}
              <div className="grid md:grid-cols-2 gap-4">
                {hebergement.images.map((image, index) => (
                  <div key={index} className="relative h-64 rounded-xl overflow-hidden shadow-lg">
                    <img 
                      src={image} 
                      alt={`${hebergement.name} ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>

              {/* Services */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Services & Équipements</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {hebergement.services.map((service, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#afab53] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">{service}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info Cards */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-[#afab53] rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Capacité d'accueil</h4>
                  <p className="text-gray-700">{hebergement.capacity}</p>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-[#afab53] rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Localisation</h4>
                  <p className="text-gray-700">{hebergement.location}</p>
                </div>
              </div>

               {/* Tarifs - Only for La Draille */}
              {hebergement.slug === 'gite-la-draille' && (
                <div className="bg-gradient-to-br from-[#afab53]/10 to-[#8a8742]/10 rounded-xl p-8 border-2 border-[#afab53]/20">
                  <h3 className="text-2xl font-bold mb-6 text-gray-900">Nos tarifs</h3>
                  <p className="text-sm text-gray-600 mb-6">Prix par personne et par nuit</p>
                  
                  {/* Chambre de 4 personnes */}
                  <div className="mb-8">
                    <div className="bg-[#afab53] text-white px-4 py-3 rounded-t-lg">
                      <h4 className="font-semibold text-lg">Chambre de 4 personnes (draps non fournis)</h4>
                    </div>
                    <div className="bg-white rounded-b-lg overflow-hidden border-2 border-[#afab53]">
                      <table className="w-full">
                        <tbody>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-3 font-medium text-gray-700">Hébergement en demi-pension <span className='text-[#afab53]'> *</span></td>
                            <td className="px-4 py-3 text-right font-bold text-[#afab53]">46,00 €</td>
                          </tr>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">Enfant (- de 10 ans)</td>
                            <td className="px-4 py-3 text-right font-bold text-[#afab53]">36,00 €</td>
                          </tr>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-3 text-gray-700">Sachet repas : pique-nique du midi</td>
                            <td className="px-4 py-3 text-right font-bold text-[#afab53]">8,00 €</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="bg-[#afab53]/5 px-4 py-3 border-t-2 border-[#afab53]/20">
                        <p className="text-sm font-medium text-gray-700 mb-2">Options (pour la durée du séjour) :</p>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">• Draps</span>
                          <span className="font-semibold text-[#afab53]">4,00 €</span>
                        </div>
                        <div className="flex justify-between items-center text-sm mt-1">
                          <span className="text-gray-600">• Linge de toilette</span>
                          <span className="font-semibold text-[#afab53]">2,00 €</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chambre privatisée pour 2 personnes revu*/}
                  <div className="mb-8">
                    <div className="bg-[#afab53] text-white px-4 py-3 rounded-t-lg">
                      <h4 className="font-semibold text-lg">Chambre privatisée pour 2 personnes (draps et linge fournis)</h4>
                    </div>
                    <div className="bg-white rounded-b-lg overflow-hidden border-2 border-[#afab53]">
                      <table className="w-full">
                        <tbody>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-3 font-medium text-gray-700">Hébergement en demi-pension <span className='text-[#afab53]'> *</span></td>
                            <td className="px-4 py-3 text-right font-bold text-[#afab53]">46,00 €</td>
                          </tr>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-3 text-gray-700">Sachet repas : pique-nique du midi</td>
                            <td className="px-4 py-3 text-right font-bold text-[#afab53]">8,00 €</td>
                          </tr>
                        </tbody>
                      </table>
                      
                    </div>
                  </div>
                  {/* Chambre privatisée pour 2 personnes */}
                  {/* <div>
                    <div className="bg-[#afab53] text-white px-4 py-3 rounded-t-lg">
                      <h4 className="font-semibold text-lg">Chambre privatisée pour 2 personnes (draps et linge fournis)</h4>
                    </div>
                    <div className="bg-white rounded-b-lg overflow-hidden border-2 border-[#afab53]">
                      <table className="w-full">
                        <tbody>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-3 font-medium text-gray-700">Pension complète*</td>
                            <td className="px-4 py-3 text-right font-bold text-[#afab53]">69,50 €</td>
                          </tr>
                          <tr className="bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">Demi-pension</td>
                            <td className="px-4 py-3 text-right font-bold text-[#afab53]">61,50 €</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="bg-[#afab53]/5 px-4 py-3 border-t-2 border-[#afab53]/20">
                        <p className="text-xs text-gray-600 italic">
                          * Hébergement en demi-pension + pique-nique du midi fourni  
                        </p>
                      </div>
                    </div>
                  </div> */}

                  <div className="mt-6 bg-white rounded-lg p-4 border border-[#afab53]/30">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <strong className="text-gray-900"><span className='text-[#afab53]'> *</span> Informations :</strong> Les tarifs sont par personne et par nuit. <br />
                      La demi-pension comprend <b> le dîner, la nuitée et le petit-déjeuner.</b> <br /> <br />
                      Pour toute réservation ou demande de devis personnalisé, n'hésitez pas à nous contacter. <br /> <br />
                      <i>La taxe de séjour municipale, par jour et par personne, incluse.</i>
                    </p>
                  </div>
                </div>
              )}

               {/* Sélection séjour */}
               {hebergement.slug === 'gite-la-draille' && (
                <section className="py-10 px-4 bg-gradient-to-br from-[#afab53] to-[#8a8742]">
                        <div className="max-w-2l mx-auto text-center text-white">
                          <h2 className="text-4xl font-bold mb-6">Découvrez nos séjours !</h2>
                          <p className="text-xl mb-10 text-white/90 leading-relaxed">
                              Nos séjours sur mesures : des expériences uniques, préparées avec soin pour vos prochaines aventures.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/sejours">
                              <Button size="lg" className="bg-white text-[#afab53] hover:bg-gray-100 text-lg px-8 py-6">
                                Nos séjours
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </section>
                
              )}

            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="bg-gradient-to-br from-[#afab53] to-[#8a8742] rounded-xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Réservez maintenant</h3>
                  <p className="mb-6 text-white/90">
                    Contactez-nous pour vérifier les disponibilités et réserver votre séjour.
                  </p>
                  <Link to={`/contact?category=${contactCategory}`}>
                    <Button className="w-full bg-white text-[#afab53] hover:bg-gray-100 mb-4">
                      Demander un devis
                    </Button>
                  </Link>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <Phone className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <p>{hebergement.phone}</p>
                        {hebergement.mobile && <p>{hebergement.mobile}</p>}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <p>{hebergement.location}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="font-semibold mb-4 text-gray-900">Moyens de paiement</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#afab53]" />
                      <span>Espèces</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#afab53]" />
                      <span>Chèques bancaires</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#afab53]" />
                      <span>Chèques vacances</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#afab53]" />
                      <span>Virement bancaire</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
};

export default HebergementDetail;